import React, { ReactElement, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useParams } from 'react-router-dom'
import Radom, { Customer, Deposit, ISubscription } from '../../state/Radom'
import Spinner from '../../components/Spinner'
import { Container } from '../../components/Animations'
import BreadCrumbs from '../../components/Breadcrumbs'
import Dropdown, { DropdownItem } from '../../components/Dropdown'
import Dots from '../../icons/Dots'
import { Section, SubTitle, SectionContent, SectionItem, PaymentDetailsSection } from '../../components/PaymentDetails'
import User from '../../state/User'
import styled from 'styled-components'
import { RADOM_COLORS } from '../../util/Constants'
import { SecondaryButton } from '../../components/Button'
import Edit from '../../icons/Edit'
import EditTagsModal from './EditTags'
import { asSimpleDateTime, errorToast, formatCurrency, useTestMode } from '../../util/Util'
import { getMethod, prodNetworks, testNetworks } from '../../util/Managed'
import { StatusBadge } from '../CheckoutSessions'
import Rotate from '../../icons/Rotate'
import { genSectionItems, MDASH } from '../../util/SectionItems'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'

// ---------------------------------------------------------------------------------------------------------------------
export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  align-items: center;
`

export const Tag = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  background-color: ${RADOM_COLORS.GRAY9};
  border-radius: 5px;
  padding: 5px 10px;
`

// ---------------------------------------------------------------------------------------------------------------------
const CustomerView = observer((): ReactElement => {
  const params = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [customer, setCustomer] = useState<Customer>()
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([])
  const [deposits, setDeposits] = useState<Deposit[]>([])

  const [isTagsModalOpen, setIsTagsModalOpen] = useState(false)
  const [testMode] = useTestMode()
  const [idCardRotationAngle, setIdCardRotationAngle] = useState(0)
  const [selfieRotationAngle, setSelfieRotationAngle] = useState(0)

  const loadCustomer = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomer(params.id, testMode ? testNetworks : prodNetworks)
      .then(s => setCustomer(s))
      .catch(e => console.error('Failed to look up checkout session', e))
      .finally(() => setIsLoading(false))
  }

  const fetchSubscriptions = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomerSubscriptions(params.id)
      .then(subs => {
        setSubscriptions(subs)
      })
      .catch(e => console.error('Failed to get this customer\'s subscriptions', e))
      .finally(() => setIsLoading(false))
  }

  const fetchDeposits = async (): Promise<void> => {
    if (!params.id) {
      return
    }

    setIsLoading(true)
    Radom.getCustomerDeposits(params.id)
      .then(deposits => {
        setDeposits(deposits)
      })
      .catch(e => console.error('Failed to get this customer\'s deposits', e))
      .finally(() => setIsLoading(false))
  }

  useEffect(() => { loadCustomer() }, [testMode])
  useEffect(() => {
    fetchSubscriptions()
    fetchDeposits()
  }, [customer])

  if (isLoading) {
    return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 140, gap: 20 }}>
      <Spinner />
    </div>
  }

  if (!customer) {
    return <></>
  }

  const isBusiness = customer.accountType === 'Business'
  const customerColumnCount = 2
  const subscriptionColumnCount = 3
  const depositColumnCount = 3

  // -------------------------------------------------------------------------------------------------------------------
  return <Container style={{ flexDirection: 'column', fontSize: 14, gap: 30 }}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

      <BreadCrumbs items={[
        { item: 'Customers', to: '/customers' },
        { item: customer.id, to: '.' }
      ]} />

      {
        false &&
        <Dropdown
          noChevron
          selectedContent={<Dots width={15} />}
          dropdownContent={
            <div style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
              <DropdownItem onClick={() => { }}>Something</DropdownItem>
            </div>
          }
        />
      }

    </div>

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Customer details
      // ---------------------------------------------------------------------------------------------------------------
    }
    <Section>
      <SubTitle style={{ fontSize: 18 }}>
        Customer details
      </SubTitle>

      <SectionContent numColumns={customerColumnCount}>
        {
          isBusiness &&
          genSectionItems([
            { heading: 'Company Name', content: customer.companyName },
            { heading: 'Company Number', content: customer.companyNumber },
            { heading: '', content: null }
          ], customerColumnCount)
        }

        <SectionItem>
          <SubTitle>ID</SubTitle>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 10,
            alignItems: 'center'
          }}>
            {customer.id}
            {
              customer.tags.length > 0 &&
              customer.tags.includes('identity_verified') &&
              <StatusBadge style={{ marginLeft: 10 }} status="active">
                KYC Verified
              </StatusBadge>
            }
          </div>
        </SectionItem>

        {
          genSectionItems([
            { heading: 'Name', content: customer.name },
            { heading: 'Email Address', content: customer.email },
            { heading: 'Address', content: customer.billingAddress },
            { heading: 'Date of Birth', content: customer.dob },
            { heading: 'Country of Residence', content: customer.residenceCountry }
          ], customerColumnCount)
        }

        {
          User.organization &&
          <SectionItem>
            <SubTitle>Total spend</SubTitle>
            <div>
              {
                Number(customer.totalSpend) <= 0
                  ? MDASH
                  : new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: User.organization?.defaultCurrency
                  }).format(Number(customer.totalSpend))
              }
            </div>
          </SectionItem>
        }

        <SectionItem>
          <SubTitle>
            <span>Tags</span>
            <SecondaryButton onClick={() => setIsTagsModalOpen(true)} style={{ padding: 0, border: 0, fontSize: 0 }}>
              <Edit stroke={RADOM_COLORS.GRAY_DARKER} style={{ height: 15 }} />
            </SecondaryButton>
          </SubTitle>
          <TagsContainer>
            {customer.tags.length <= 0 && MDASH}
            {customer.tags.map((t, i) => <Tag key={i}>{t}</Tag>)}
          </TagsContainer>

        </SectionItem>

        {
          genSectionItems([
            { heading: 'Phone', content: customer.phone },
            { heading: 'Telegram', content: customer.telegram },
            { heading: 'Discord', content: customer.discord }
          ], customerColumnCount)
        }
      </SectionContent>
    </Section>

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Customer KYC info
      // ---------------------------------------------------------------------------------------------------------------
      customer.tags.length > 0 &&
      customer.tags.includes('identity_verified') &&
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Verfications
        </SubTitle>
        <SectionContent
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between', // 添加这一行
            gap: 20,
            width: '100%' // 建议加上宽度控制
          }}
        >

          <SectionItem style={{ flex: 1 }}>
            <SubTitle style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}>Passport/IDcard
              <Rotate
                width={15}
                height={15}
                onClick={() => {
                  setIdCardRotationAngle(idCardRotationAngle + 90)
                  console.log('idCardRotationAngle', idCardRotationAngle)
                }}
              />
            </SubTitle>
            <div style={{ padding: 10, overflow: 'hidden' }}>
              {customer.kycData?.idCardUrl || customer.kycData?.passportUrl
                ? <img
                  src={customer.kycData?.idCardUrl || customer.kycData?.passportUrl}
                  onClick={() =>
                    window.open(customer.kycData?.idCardUrl || customer.kycData?.passportUrl, '_blank')
                  }
                  style={{
                    transform: `rotate(${idCardRotationAngle}deg)`,
                    maxHeight: 300,
                    height: 'auto',
                    width: '100%',
                    objectFit: 'contain',
                    borderRadius: 10
                  }}
                />
                : null}
            </div>
          </SectionItem>
          <SectionItem style={{ flex: 1 }}>
            <SubTitle style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer'
            }}>Selfie
              <Rotate
                width={15}
                height={15}
                onClick={() => {
                  setSelfieRotationAngle(selfieRotationAngle + 90)
                  console.log('selfieRotationAngle', selfieRotationAngle)
                }}
              />
            </SubTitle>
            <div
              style={{
                width: 'auto',
                maxHeight: 300,
                overflow: 'hidden',
                padding: 10
              }}
            >
              <img
                src={customer.kycData?.selfieUrl}
                onClick={() => window.open(customer.kycData?.selfieUrl, '_blank')}
                style={{
                  transform: `rotate(${selfieRotationAngle}deg)`,
                  maxHeight: 300,
                  height: 'auto',
                  width: '100%',
                  objectFit: 'contain',
                  borderRadius: 10
                }}
              />
            </div>
          </SectionItem>
        </SectionContent>
      </Section>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Payment info
      // ---------------------------------------------------------------------------------------------------------------
      customer.payments.length > 0 &&

      <Section>
        <SubTitle style={{ fontSize: 18 }}>
          Payments
        </SubTitle>
        {
          customer.payments.map((p, i) => {
            if (p.managed) {
              return <PaymentDetailsSection
                short
                key={i}
                payment={{ managed: p.managed }}
                netPayment={(Number(p.managed.amount) - Number(p.managed.networkFee)) * 0.995} />
            } else if (p.openBanking) {
              return <PaymentDetailsSection
                short
                key={i}
                payment={{ openBanking: p.openBanking }}
                netPayment={0} />
            } else {
              return null
            }
          })
        }
      </Section>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Subscriptions
      // ---------------------------------------------------------------------------------------------------------------
      subscriptions && subscriptions.length > 0 &&
      <Section style={{
        backgroundColor: subscriptions && subscriptions.length > 0 ? RADOM_COLORS.GRAY_LIGHTEST : RADOM_COLORS.WHITE
      }}>
        <SubTitle style={{ fontSize: 18 }}>
          Subscriptions
        </SubTitle>
        {
          subscriptions.reduce<React.ReactElement[]>((acc, sub, idx) => {
            const items = <Section style={{ backgroundColor: RADOM_COLORS.WHITE }}>
              <SectionContent numColumns={subscriptionColumnCount}>
                {genSectionItems([
                  { heading: 'Subscription Id', content: sub.id },
                  { heading: 'Created', content: asSimpleDateTime(sub.createdAt) },
                  { heading: 'Status', content: sub.status },
                  { heading: 'Checkout Type', content: sub.associatedEntityType },
                  { heading: 'Product', content: sub.tags?.name },

                  sub.cancelledAt
                    ? { heading: 'Cancelled At', content: asSimpleDateTime(sub.cancelledAt) }
                    : sub.expiredAt
                      ? { heading: 'Expired At', content: asSimpleDateTime(sub.expiredAt) }
                      : { heading: '', content: null },

                  sub.subscriptionType
                    ? sub.subscriptionType.automatedEVMSubscription
                      ? { heading: 'Email Address', content: sub.subscriptionType.automatedEVMSubscription?.emailAddress }
                      : { heading: 'Email Address', content: sub.subscriptionType.emailInvoiceSubscription?.emailAddress }
                    : { heading: 'Email Address', content: null },

                  { heading: 'Next Billing Date', content: asSimpleDateTime(sub.nextBillingDateAt) },
                  <SectionItem key={idx}>
                    <SubTitle>Amount</SubTitle>
                    <PaymentMethodDisplay
                      method={{ network: sub.network, token: sub.token }}
                      excludeName
                      disableTooltip={false} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {formatCurrency(Number(sub.amount), sub.currency)}
                      <span style={{ fontWeight: 300, opacity: 0.75 }}>{sub.period}</span>
                    </div>
                  </SectionItem>
                ], subscriptionColumnCount)}
              </SectionContent>
            </Section>

            return acc.concat(items)
          }, [])
        }
      </Section>
    }

    {
      // ---------------------------------------------------------------------------------------------------------------
      // Deposits
      // ---------------------------------------------------------------------------------------------------------------
      deposits && deposits.length > 0 &&
      <Section style={{
        backgroundColor: deposits && deposits.length > 0 ? RADOM_COLORS.GRAY_LIGHTEST : RADOM_COLORS.WHITE
      }}>
        <SubTitle style={{ fontSize: 18 }}>
          Deposits
        </SubTitle>
        {
          deposits.reduce<React.ReactElement[]>((acc, dep, idx) => {
            const items = <Section style={{ backgroundColor: RADOM_COLORS.WHITE }}>
              <SectionContent numColumns={depositColumnCount}>
                {genSectionItems([
                  { heading: 'Deposit Id', content: dep.id },
                  { heading: 'Type', content: dep.depositMethod.Crypto ? 'Crypto deposit' : 'Fiat deposit' },
                  { heading: 'Deposited', content: asSimpleDateTime(dep.depositedAt) },
                  <SectionItem key={idx}>
                    <SubTitle>Net Amount</SubTitle>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <img src={getMethod(
                        dep.depositMethod.Crypto?.network ||
                        dep.depositMethod.Fiat?.onrampNetwork || '',
                        dep.depositMethod.Crypto?.token ||
                        dep.depositMethod.Fiat?.onrampToken
                      ).logo}
                      style={{
                        width: 16,
                        height: 16
                      }}
                      />
                      {dep.netAmount}
                    </div>
                  </SectionItem>,
                  <SectionItem key={idx + 1}>
                    <SubTitle>Gross Amount</SubTitle>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <img src={getMethod(
                        dep.depositMethod.Crypto?.network ||
                        dep.depositMethod.Fiat?.onrampNetwork || '',
                        dep.depositMethod.Crypto?.token ||
                        dep.depositMethod.Fiat?.onrampToken
                      ).logo}
                      style={{
                        width: 16,
                        height: 16
                      }}
                      />
                      {dep.grossAmount}
                    </div>
                  </SectionItem>,
                  <SectionItem key={idx + 2}>
                    <SubTitle>Fee</SubTitle>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <img src={getMethod(
                        dep.depositMethod.Crypto?.network ||
                        dep.depositMethod.Fiat?.onrampNetwork || '',
                        dep.depositMethod.Crypto?.token ||
                        dep.depositMethod.Fiat?.onrampToken
                      ).logo}
                      style={{
                        width: 16,
                        height: 16
                      }}
                      />
                      {dep.fee}
                    </div>
                  </SectionItem>,
                  ...(dep.depositMethod.Fiat && dep.fiatGrossAmount
                    ? [
                      <SectionItem key={idx + 2}>
                        <SubTitle>Fiat Gross Amount</SubTitle>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <img src={getMethod(
                            'Fiat',
                            dep.depositMethod.Fiat.fiatCurrency
                          ).logo}
                          style={{
                            width: 16,
                            height: 16
                          }}
                          />
                          {dep.fiatGrossAmount}
                        </div>
                      </SectionItem>
                    ]
                    : [])
                ], depositColumnCount)}
              </SectionContent>
            </Section>

            return acc.concat(items)
          }, [])
        }
      </Section>
    }

    {
      isTagsModalOpen &&
      <EditTagsModal
        onClose={() => setIsTagsModalOpen(false)}
        isOpen={isTagsModalOpen}
        tags={customer.tags}
        onSave={async tags => {
          try {
            await Radom.updateCustomer(customer.id, { tags })
            setIsTagsModalOpen(false)
            loadCustomer()
          } catch (err) {
            errorToast(err.response?.data?.error || err.message)
          }
        }} />
    }
  </Container>
})

export default CustomerView
